export const CONDITION_FIELDS = [
  { value: 'total_spent', label: 'Total Spend', uiType: 'operator_value' },
  { value: 'orders_count', label: 'Total Orders', uiType: 'operator_value' },
  { value: 'last_order_days_ago', label: 'Last order is more than X days ago', uiType: 'days_only' },
  { value: 'industry', label: 'Industry is one of', uiType: 'multiselect_industry' },
  { value: 'ordered_from_location', label: 'Ordered from location', uiType: 'multiselect_location' },
  { value: 'orders_from_restaurant', label: 'Ordered from restaurant', uiType: 'multiselect_restaurant' },
];

/**
 * Compare two condition values for equality (handles arrays, numbers, type coercion).
 */
function conditionValuesEqual(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort((x, y) => (x < y ? -1 : x > y ? 1 : 0));
    const sortedB = [...b].sort((x, y) => (x < y ? -1 : x > y ? 1 : 0));
    return sortedA.every((val, i) => {
      const vb = sortedB[i];
      if (typeof val === 'number' && typeof vb === 'number') return val === vb;
      return String(val) === String(vb);
    });
  }
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (typeof a === 'number' && typeof b === 'string') return a === Number(b);
  if (typeof a === 'string' && typeof b === 'number') return Number(a) === b;
  return a === b;
}

/**
 * Two conditions are duplicates if they have the same field, operator (when applicable), and value.
 * E.g. "Ordered from location X" three times = duplicate. "Total Spend > $1000" and "Total Spend > $1001" = not duplicate.
 */
export function areConditionsDuplicate(c1, c2) {
  if (c1.field !== c2.field) return false;
  if ((c1.operator || c2.operator) && c1.operator !== c2.operator) return false;
  return conditionValuesEqual(c1.value, c2.value);
}

export function hasDuplicateConditions(conditions) {
  if (!conditions || conditions.length < 2) return false;
  for (let i = 0; i < conditions.length; i++) {
    for (let j = i + 1; j < conditions.length; j++) {
      if (areConditionsDuplicate(conditions[i], conditions[j])) return true;
    }
  }
  return false;
}

/**
 * Check if a condition would be a duplicate when added to the list, excluding a specific index (for updates).
 */
export function wouldBeDuplicate(conditions, newCond, excludeIndex = -1) {
  if (!conditions) return false;
  return conditions.some((c, i) => i !== excludeIndex && areConditionsDuplicate(c, newCond));
}

export const CONDITION_OPERATORS = [
  { value: 'gt', label: 'greater than' },
  { value: 'gte', label: 'greater than or equal to' },
  { value: 'lt', label: 'less than' },
  { value: 'lte', label: 'less than or equal to' },
  { value: 'eq', label: 'equal to' },
];

export const formatConditionForDisplay = (c) => {
  const fieldLabels = {
    total_spent: 'Total Spend',
    orders_count: 'Total Orders',
    last_order_days_ago: 'Last order is more than X days ago',
    industry: 'Industry is one of',
    ordered_from_location: 'Ordered from location',
    orders_from_restaurant: 'Ordered from restaurant',
  };
  const opLabels = { gt: 'is greater than', gte: 'is at least', lt: 'is less than', lte: 'is at most', eq: 'equals' };
  const field = fieldLabels[c.field] || c.field;
  const op = opLabels[c.operator] || c.operator;

  if (c.field === 'total_spent') return `${field} ${op} $${Number(c.value).toLocaleString()}`;
  if (c.field === 'orders_count') return `${field} ${op} ${c.value}`;
  if (c.field === 'last_order_days_ago') return `Last order is more than ${c.value ?? 30} days ago`;
  if (c.field === 'industry') {
    const industries = Array.isArray(c.value) ? c.value : (c.value ? [c.value] : []);
    return industries.length ? `Industry is one of: ${industries.join(', ')}` : 'Industry is one of';
  }
  if (c.field === 'ordered_from_location') {
    const arr = Array.isArray(c.value) ? c.value : (c.value ? [c.value] : []);
    if (arr.length === 0) return 'Ordered from location';
    const labels = arr.every((x) => typeof x === 'string') ? arr : null;
    return labels ? `Ordered from: ${labels.join(', ')}` : `Ordered from: ${arr.length} location${arr.length !== 1 ? 's' : ''}`;
  }
  if (c.field === 'orders_from_restaurant') {
    const arr = Array.isArray(c.value) ? c.value : (c.value ? [c.value] : []);
    if (arr.length === 0) return 'Ordered from restaurant';
    const labels = arr.every((x) => typeof x === 'string') ? arr : null;
    return labels ? `Ordered from: ${labels.join(', ')}` : `Ordered from: ${arr.length} restaurant${arr.length !== 1 ? 's' : ''}`;
  }
  return `${field} ${op} ${c.value}`;
};
